import { captureMessage } from '@sentry/nextjs';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { BROADCAST_PAYLOADS, OIDCChanel } from '@/constant/broadcast';
import { EXPIRES_AT_SESSION_BUFFER_SEC, OIDC_CHECK_INTERVAL_MS, OIDC_QUERY_PARAMS } from '@/constant/oidc';
import { getGlobalArgs } from '@/root/global';
import { OIDCMessage, OIDCMessageType } from '@/types/broadcast';
import { isNil, mergeRight } from '@/utils/common';
import { checkUserSession, handleSilentRenew, isExpiredSession, isSessionValid } from '@/utils/oidc';
import { getFullCurrentPath } from '@/utils/routes';

export const useUpdateCookieToken = () => {
  const [authChecked, setAuthChecked] = useState(false);
  const { user, isLoading } = useAuth();

  const updateCookieReq = async (token?: string) => {
    try {
      await fetch(getGlobalArgs('NEXT_PUBLIC_SET_AUTH_TOKEN_COOKIE_URL', ''), {
        body: JSON.stringify({ token }),
        method: 'POST',
      });
      setAuthChecked(true);
    } catch (err) {
      captureMessage(`failed to update cookie token: ${err}`, { level: 'error' });
      setAuthChecked(true);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      updateCookieReq(user?.id_token);
    }
  }, [isLoading, user?.id_token]);

  return authChecked;
};

export const useOIDCAuth = (authRequired?: boolean, checkSessionInterval = OIDC_CHECK_INTERVAL_MS) => {
  const {
    isAuthenticated,
    user,
    isLoading,
    signinRedirect,
    signoutRedirect,
    settings,
    signinSilent,
    removeUser,
    clearStaleState,
    revokeTokens,
    events,
  } = useAuth();
  useUpdateCookieToken();

  const [isAuthByStateReq, setIsAuthByStateReq] = useState<null | boolean>(null);

  useEffect(() => {
    const onMessage = async (event: MessageEvent<OIDCMessage<OIDCMessageType>>) => {
      const dataType = event.data.type;

      if (dataType === 'logout') {
        if (authRequired) {
          await signoutRedirect({
            id_token_hint: user?.id_token,
            post_logout_redirect_uri: authRequired
              ? getGlobalArgs('NEXT_PUBLIC_OIDC_REDIRECT_URI')
              : getFullCurrentPath(window.location),
          });
        }

        await removeUser();
        await clearStaleState();
        await revokeTokens();
      }

      if (dataType === 'updateUser') {
        const updatedUser = event.data.user;
        if (!updatedUser) return;
        return await events.load(updatedUser);
      }
      return;
    };

    OIDCChanel.addEventListener('message', onMessage);
    return () => OIDCChanel.removeEventListener('message', onMessage);
  }, []);

  const expires_at = user?.expires_at;

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const code = searchParams.get(OIDC_QUERY_PARAMS.code);

  useEffect(() => {
    if (!user) return;
    OIDCChanel.postMessage(mergeRight(BROADCAST_PAYLOADS.OIDC.updateUser, { user }));
  }, [expires_at]);

  useEffect(() => {
    if (Boolean(code)) {
      const params = new URLSearchParams(searchParams);
      params.delete(OIDC_QUERY_PARAMS.code);
      params.delete(OIDC_QUERY_PARAMS.state);
      return router.replace(`${pathname}${params.toString() ? `?${params}` : ''}`);
    }

    const onFailedCheck = async () => {
      await removeUser();
      await revokeTokens();
      await clearStaleState();
      setIsAuthByStateReq(false);

      if (authRequired) {
        return signinRedirect({ redirect_uri: getFullCurrentPath(window.location) });
      }
    };
    const onSuccessCheck = () => {
      setIsAuthByStateReq(true);
    };

    checkUserSession({ onFailedCheck, onSuccessCheck });
  }, []);

  useEffect(() => {
    if (!isLoading && isNil(user) && Boolean(isAuthByStateReq)) {
      signinRedirect({ redirect_uri: getFullCurrentPath(window.location) });
      return;
    }
  }, [isAuthByStateReq, user, isLoading]);

  useEffect(() => {
    if (!settings.automaticSilentRenew || !isAuthenticated) return;

    const checkAndRenewSession = () => {
      if (isExpiredSession(expires_at, EXPIRES_AT_SESSION_BUFFER_SEC)) {
        if (!authRequired) return handleSilentRenew(signinSilent);
        return handleSilentRenew(signinSilent, signoutRedirect);
      }
    };

    const interval = setInterval(checkAndRenewSession, checkSessionInterval);
    return () => clearInterval(interval);
  }, [isAuthenticated, settings.automaticSilentRenew, expires_at, authRequired]);

  useEffect(() => {
    if (!isAuthenticated || !authRequired) return;

    const interval = setInterval(async () => {
      const isValidSession = await isSessionValid(expires_at, EXPIRES_AT_SESSION_BUFFER_SEC);
      if (isValidSession || typeof isValidSession === 'object') return;

      if (!authRequired) {
        await removeUser();
        await revokeTokens();
        return await clearStaleState();
      }
      return signoutRedirect({
        post_logout_redirect_uri: !authRequired
          ? getGlobalArgs('NEXT_PUBLIC_OIDC_REDIRECT_URI')
          : getFullCurrentPath(window.location),
      });
    }, checkSessionInterval);

    return () => clearInterval(interval);
  }, [authRequired, isAuthenticated, expires_at, checkSessionInterval]);

  return {
    isLoading,
    isAuthenticated,
    user,
  };
};
